import { FC, ReactNode, memo, useEffect, useRef, useState } from 'react'
import { useElementSize, useEventListener } from 'usehooks-ts'
import Iconfont from 'components/Iconfont'
import { styled } from '@mui/material'
import { Flex, FlexStart } from 'styles'

const MarketInfoWrap = styled(FlexStart)`
  flex: 1;
  overflow: hidden;
  position: relative;
`
const MarketInfoInner = styled(FlexStart)`
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  position: relative;
  ::-webkit-scrollbar {
    display: none;
  }
`
const MarketInfoScroller = styled(FlexStart)`
  flex: 1;
  gap: 24px;
  ${({ theme }) => theme.breakpoints.down('lg')} {
    display: grid;
    grid-template-columns: 0.8fr 1.1fr;
    grid-row-gap: 12px;
    grid-column-gap: 24px;
  }
`
const ScrollLeftBtn = styled(Flex)`
  position: absolute;
  left: 0;
  top: 0;
  width: 58px;
  height: 100%;
  cursor: pointer;
  align-items: center;
  z-index: 1;
  background: linear-gradient(
    to right,
    ${(props) => props.theme.color.bg} 42.24%,
    transparent 95.69%
  );
  .iconfont {
    color: rgba(98, 109, 116, 1);
  }
  ${({ theme }) => theme.breakpoints.down('lg')} {
    display: none;
  }
`
const ScrollRightBtn = styled(ScrollLeftBtn)`
  right: 0;
  left: inherit;
  justify-content: end;
  background: linear-gradient(
    to left,
    ${(props) => props.theme.color.bg} 42.24%,
    transparent 95.69%
  );

  .iconfont {
    &:hover {
      color: ${({ theme }) => theme.palette.primary.main};
    }
  }
  ${({ theme }) => theme.breakpoints.down('lg')} {
    display: none;
  }
`

export const MarketInfo: FC<{
  children: ReactNode
}> = memo(({ children }) => {
  const wrapRef = useRef<HTMLDivElement>(null)

  const [hasMore, setMore] = useState(true)

  const [showLeft, setLeft] = useState(false)
  const [showRight, setRight] = useState(true)

  const [innerRef, { width = 0 }] = useElementSize()

  useEffect(() => {
    if (wrapRef.current) {
      if (wrapRef.current.offsetWidth < width) {
        setMore(true)
        setRight(true)
      } else {
        setMore(false)
      }
    }
  }, [width, wrapRef])

  const onScroll = (event: any) => {
    const scrollLeft = event.target.scrollLeft

    if (scrollLeft + wrapRef.current?.offsetWidth + 2 >= width) {
      setRight(false)
    } else {
      setRight(true)
    }
    if (scrollLeft === 0) {
      setLeft(false)
    } else {
      setLeft(true)
    }
  }

  useEventListener('scroll', onScroll, wrapRef)

  const moveHandler = (toRight: boolean) => {
    const scrollLeft = wrapRef.current?.scrollLeft ?? 0
    const width = wrapRef.current?.offsetWidth ?? 200

    if (toRight) {
      wrapRef.current?.scrollTo({
        left: scrollLeft + width,
        behavior: 'smooth',
      })
    } else {
      wrapRef.current?.scrollTo({
        left: scrollLeft - width,
        behavior: 'smooth',
      })
    }
  }

  return (
    <MarketInfoWrap>
      {hasMore && showLeft && (
        <ScrollLeftBtn onClick={() => moveHandler(false)}>
          <Iconfont name="icon-ArrowLeft" size={16} />
        </ScrollLeftBtn>
      )}
      <MarketInfoInner ref={wrapRef}>
        <MarketInfoScroller ref={innerRef}>{children}</MarketInfoScroller>
      </MarketInfoInner>
      {hasMore && showRight && (
        <ScrollRightBtn onClick={() => moveHandler(true)}>
          <Iconfont name="icon-ArrowRight" size={16} />
        </ScrollRightBtn>
      )}
    </MarketInfoWrap>
  )
})
